export const useFilters = () => {
  const isFiltering = ref(false);
  const searchTerm = ref("");
  const filtersFromURI = ref("");
  const filtersPayload = ref<FiltersPayload | null>(
    {} as FiltersPayload | null
  );
  const { getUserCollectionFilters } = useUserController();
  const { getCollectibleFilters } = useCollectiblesController();
  const appStateStore = useAppStateStore();
  const { locale } = useI18n();

  const userStore = useUserStore();
  const route = useRoute();
  const router = useRouter();
  const userId = (route.params.id as string) ?? userStore.id;
  const sortBy = ref<SortKeys>("PRICELOWHIGH");
  const orderBy = ref<"ASC" | "DESC">("DESC");
  const selectedFilters = reactive<SelectedFilters>({
    range: {},
    options: {},
    sort: sortBy.value,
    order: orderBy.value,
    search: "",
    lang: locale.value,
  });
  const appliedFiltersCount = ref(0);

  const filters = reactive<Filters>({ range: {}, options: {} });

  const propertyType = computed(() => {
    return route.path.includes("stack") ? ["LEGACY"] : ["CORE", "STATS"];
  });

  const getFilters = async () => {
    filters.options = {};
    filters.range = {};

    if (route.path.includes("market")) {
      filtersPayload.value = await getCollectibleFilters(
        appStateStore.currentMerchant,
        route.params.setId as string,
        route.query.listed === "false" ? false : true,
        propertyType.value
      );
    } else if (
      route.path.includes("collection") ||
      route.path.includes("user")
    ) {
      filtersPayload.value = await getUserCollectionFilters(
        userId,
        appStateStore.currentMerchant,
        propertyType.value
      );
    }
    if (!filtersPayload.value) return;
    if (Object.keys(filtersPayload.value).length < 1) return;
    initFilters(filtersPayload.value);
  };

  const initFilters = (filtersRes: FiltersPayload) => {
    Object.keys(filtersRes).forEach((key) => {
      if (filtersRes[key].type === "OPTIONS") {
        const options = {} as FiltersOptions;
        filtersRes[key].options.forEach((option) => {
          options[option] = false;
        });
        if (!filtersRes[key].options.length) return;
        filters.options = { ...filters.options, [key]: options };
      } else if (filtersRes[key].type === "NUMERIC_RANGE") {
        const range: FiltersRange = {
          min: filtersRes[key].min,
          max: filtersRes[key].max,
          selectedMin: filtersRes[key].min,
          selectedMax: filtersRes[key].max,
        };
        if (filtersRes[key].min === filtersRes[key].max) return;
        filters.range = { ...filters.range, [key]: range };
      }
    });
  };
  const applyFilters = async () => {
    isFiltering.value = true;

    Object.keys(filters.options).forEach((filterKey) => {
      const options: SelectedFiltersOptions = {};
      Object.keys(filters.options[filterKey]).forEach((value) => {
        if (filters.options[filterKey][value]) {
          options[value] = true;
        }
      });
      if (Object.keys(options).length > 0) {
        selectedFilters.options = {
          ...selectedFilters.options,
          [filterKey]: options,
        };
      }
    });

    Object.keys(filters.range).forEach((filterKey) => {
      if (
        filters.range[filterKey].selectedMin === filters.range[filterKey].min &&
        filters.range[filterKey].selectedMax === filters.range[filterKey].max
      )
        return;
      const range = {} as SelectedFiltersRange;
      if (
        filters.range[filterKey].selectedMin !== filters.range[filterKey].min
      ) {
        range.selectedMin = filters.range[filterKey].selectedMin.toString();
      }
      if (
        filters.range[filterKey].selectedMax !== filters.range[filterKey].max
      ) {
        range.selectedMax = filters.range[filterKey].selectedMax.toString();
      }
      selectedFilters.range = { ...selectedFilters.range, [filterKey]: range };
    });

    const filterParams = toQueryParams(
      selectedFilters,
      sortBy.value,
      searchTerm.value
    );

    await delay(1);
    isFiltering.value = false;
    router.push({ query: { filter: filterParams } });
  };

  const clearFilters = () => {
    resetFilters();
    router.push({ query: {} });
  };

  const toQueryParams = (
    selectedFilters: SelectedFilters,
    sort: string,
    searchTerm: string
  ) => {
    const queryParams = new URLSearchParams();
    if (Object.keys(selectedFilters).length) {
      Object.keys(selectedFilters.options).forEach((key) => {
        Object.keys(selectedFilters.options[key]).forEach((value) => {
          if (queryParams.has(key)) {
            const existingValue = queryParams.get(key);
            queryParams.set(key, `${existingValue},${value}`);
          } else {
            queryParams.append(key, value);
          }
        });
      });
    }
    if (Object.keys(selectedFilters.range).length) {
      Object.keys(selectedFilters.range).forEach((key) => {
        if (selectedFilters.range[key].selectedMin) {
          queryParams.append(
            `${key}_min`,
            selectedFilters.range[key].selectedMin
          );
        }
        if (selectedFilters.range[key].selectedMax) {
          queryParams.append(
            `${key}_max`,
            selectedFilters.range[key].selectedMax
          );
        }
      });
    }
    queryParams.append("sort", collectionSortNames[sort].query);
    queryParams.append("order", collectionSortNames[sort].order);
    searchTerm && queryParams.set("search", searchTerm);
    searchTerm && queryParams.set("lang", locale.value);
    return "&" + queryParams.toString();
  };

  const countSelectedFilters = () => {
    let count = 0;
    Object.keys(filters.options).forEach((key) => {
      Object.keys(filters.options[key]).forEach((value) => {
        if (filters.options[key][value]) count++;
      });
    });
    Object.keys(filters.range).forEach((key) => {
      if (filters.range[key].selectedMin !== filters.range[key].min) count++;
      if (filters.range[key].selectedMax !== filters.range[key].max) count++;
    });
    if (searchTerm.value) count++;
    return count;
  };

  const resetFilters = () => {
    filters.options = {};
    filters.range = {};
    selectedFilters.options = {};
    selectedFilters.range = {};
    sortBy.value = "RECEIVED_TIME";
    searchTerm.value = "";
    getFilters();
  };

  const getFiltersFromUri = () => {
    if (route.query.filter) {
      filtersFromURI.value = route.query.filter as string;
    } else {
      filtersFromURI.value = "";
    }
  };

  const setSortDefault = () => {
    if (route.path.includes("market") && route.path.includes("stack")) {
      sortBy.value = "PRICELOWHIGH";
      orderBy.value = "ASC";
    } else if (route.path.includes("market")) {
      sortBy.value = "LISTED_TIME";
      orderBy.value = "DESC";
    } else if (route.path.includes("collection")) {
      sortBy.value = "RECEIVED_TIME";
      orderBy.value = "DESC";
    } else if (route.path.includes("user")) {
      sortBy.value = "LISTED_TIME";
      orderBy.value = "DESC";
    } else {
      sortBy.value = "RECEIVED_TIME";
      orderBy.value = "DESC";
    }
  };

  const setSortFromUri = () => {
    if (!route.query.filter) {
      setSortDefault();
      return;
    } else {
      const queryParams = new URLSearchParams(route.query.filter as string);
      const sort = queryParams.get("sort") as SortKeys;
      const order = queryParams.get("order") as "ASC" | "DESC";
      const sortKey = Object.keys(collectionSortNames).find(
        (key) =>
          collectionSortNames[key].query === sort &&
          collectionSortNames[key].order === order
      );
      if (sortKey) {
        sortBy.value = sortKey as SortKeys;
        orderBy.value = order;
      } else {
        setSortDefault();
      }
      filtersFromURI.value = filtersFromURI.value.replace(
        `sort=${sort}&order=${order}`,
        ""
      );
    }
  };

  const setFiltersBasedOnFiltersFromUri = () => {
    const queryParams = new URLSearchParams(filtersFromURI.value);
    queryParams.forEach((value, key) => {
      if (
        key === "sort" ||
        key === "order" ||
        key === "search" ||
        key === "lang"
      )
        return;
      if (key.includes("_min") || key.includes("_max")) {
        const filterKey = key.replace("_min", "").replace("_max", "");
        if (!filters.range[filterKey]) return;
        if (key.includes("_min")) {
          filters.range[filterKey].selectedMin =
            Number(value) < filters.range[filterKey].min
              ? filters.range[filterKey].min
              : Number(value);
        }
        if (key.includes("_max")) {
          filters.range[filterKey].selectedMax = Number(value);
        }
        return;
      } else {
        if (!filters.options[key]) {
          filters.options[key] = {};
        }
        if (value.includes(",")) {
          const values = value.split(",");
          values.forEach((val) => {
            filters.options[key][val] = true;
          });
        } else {
          filters.options[key][value] = true;
        }
      }
    });
    searchTerm.value = queryParams.get("search") ?? "";
  };

  onMounted(async () => {
    getFiltersFromUri();
    setSortFromUri();
    await getFilters();
    setFiltersBasedOnFiltersFromUri();
    appliedFiltersCount.value = countSelectedFilters();
  });

  return {
    filters,
    isFiltering,
    sortBy,
    searchTerm,
    appliedFiltersCount,
    applyFilters,
    clearFilters,
  };
};
